<script setup>
/**
 * 收费规则编辑弹窗。表单值在打开时从 `policy` 拷一份，保存前不碰列表；
 * 请求由页面发，这里只负责校验和把干净的 body 交出去。
 */
import { computed, reactive, watch } from 'vue'
import { Button, FormField, Input, Modal, SegmentedControl } from '@drama/ui'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  open: { type: Boolean, required: true },
  policy: { type: Object, default: null },
  saving: { type: Boolean, required: true },
  error: { type: String, required: true },
})
const emit = defineEmits(['update:open', 'submit'])
const { t } = useI18n()

const form = reactive({ points: '', timezone: '', enabled: 'on' })

watch(() => props.open, (open) => {
  if (!open) return
  form.points = String(props.policy.points)
  form.timezone = props.policy.timezone === null ? '' : props.policy.timezone
  form.enabled = props.policy.enabled ? 'on' : 'off'
})

const isDaily = computed(() => props.policy !== null && props.policy.period_kind === 'daily')
const enabledOptions = computed(() => [
  { value: 'on', label: t('visualGameAdmin.billing.enabledState.on') },
  { value: 'off', label: t('visualGameAdmin.billing.enabledState.off') },
])
const pointsError = computed(() => {
  const points = Number(form.points)
  return Number.isInteger(points) && points >= 1 ? '' : t('visualGameAdmin.billing.policies.errors.points')
})
const timezoneError = computed(() => (
  isDaily.value && form.timezone.trim() === '' ? t('visualGameAdmin.billing.policies.errors.timezone') : ''
))
const valid = computed(() => pointsError.value === '' && timezoneError.value === '')

function submit() {
  if (!valid.value) return
  emit('submit', {
    points: Number(form.points),
    timezone: isDaily.value ? form.timezone.trim() : null,
    enabled: form.enabled === 'on',
  })
}
</script>

<template>
  <Modal
    :open="open"
    :title="policy === null ? '' : t('visualGameAdmin.billing.policies.form.title', { action: t(`visualGameAdmin.billing.actionNames.${policy.action}`) })"
    width="480px"
    @update:open="emit('update:open', $event)"
  >
    <form v-if="policy !== null" class="vga-form" @submit.prevent="submit">
      <p class="vga-muted vga-form__lead">{{ t(`visualGameAdmin.billing.actionHints.${policy.action}`) }}</p>
      <FormField :label="t('visualGameAdmin.billing.policies.form.points')" :error="pointsError" required>
        <Input v-model="form.points" type="number" />
      </FormField>
      <FormField
        v-if="isDaily"
        :label="t('visualGameAdmin.billing.policies.form.timezone')"
        :hint="t('visualGameAdmin.billing.policies.form.timezoneHint')"
        :error="timezoneError"
        required
      >
        <Input v-model="form.timezone" placeholder="America/Los_Angeles" />
      </FormField>
      <FormField :label="t('visualGameAdmin.billing.policies.form.enabled')" :hint="t('visualGameAdmin.billing.policies.form.enabledHint')">
        <SegmentedControl v-model="form.enabled" :options="enabledOptions" />
      </FormField>
      <p v-if="error" class="vga-error vga-form__error" role="alert">{{ error }}</p>
    </form>
    <template #footer>
      <Button variant="ghost" :disabled="saving" @click="emit('update:open', false)">{{ t('visualGameAdmin.common.cancel') }}</Button>
      <Button :loading="saving" :disabled="!valid" @click="submit">{{ t('visualGameAdmin.common.save') }}</Button>
    </template>
  </Modal>
</template>
